import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Divider from "@material-ui/core/Divider";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import { makeStyles } from "@material-ui/core/styles";
import ArchiveIcon from "@material-ui/icons/Archive";
import DeleteIcon from "@material-ui/icons/Delete";
import EmailIcon from "@material-ui/icons/Email";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";

import General from "./General";
import AlertDialog from "../Alert";
import LoadingButton from "../../Buttons/Loading";
import ConditionalTooltip from "../../ConditionalTooltip";
import * as constants from "../../../constants";
import * as appsyncOperations from "../../../utils/appsyncOperations";
import { isUserRole } from "../../../utils/user";
import * as colors from "../../../styles/colors";
import { setClient } from "../../../store/features/clients/slice";
import ChangeClientPasswordDialog from "../ChangeClientPassword";

const ClientDetailsDialog = ({ isOpen, onClose, id }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const client = useSelector((state) => state.clients.entities[id]);
  const formik = useFormik({
    initialValues: {
      email: client.email,
      firstName: client.firstName,
      id: client.id,
      isArchived: client.isArchived,
      isDeleted: client.isDeleted,
      lastName: client.lastName,
      propertyIds: [...client.propertyIds],
      serviceProviderId: client.serviceProviderId,
      updatedAt: client.updatedAt,
    },
    validationSchema: yup.object({
      firstName: yup
        .string()
        .max(80, "Must be 80 characters or less.")
        .required("Required"),
      lastName: yup
        .string()
        .max(80, "Must be 80 characters or less.")
        .required("Required"),
    }),
    onSubmit: async (values, actions) => {
      try {
        const updatedClient = await appsyncOperations.updateClient(values);
        formik.resetForm({
          values: {
            email: updatedClient.email,
            firstName: updatedClient.firstName,
            id: updatedClient.id,
            isArchived: updatedClient.isArchived,
            isDeleted: updatedClient.isDeleted,
            lastName: updatedClient.lastName,
            propertyIds: [...updatedClient.propertyIds],
            serviceProviderId: updatedClient.serviceProviderId,
            updatedAt: updatedClient.updatedAt,
          },
        });
        dispatch(setClient({ client: updatedClient }));
        enqueueSnackbar("Edit saved!", { variant: "success" });
      } catch (error) {
        if (typeof error === "string") {
          enqueueSnackbar(error, { variant: "error" });
        } else {
          enqueueSnackbar("An unexpected error occurred.", {
            variant: "error",
          });
        }
      }
      actions.setSubmitting(false);
    },
  });
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    content: "",
    onYes: () => null,
    onNo: () => null,
    onClose: () => null,
  });
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedContent, setSelectedContent] = useState(content.GENERAL);

  useEffect(() => {
    if (!formik.dirty) {
      formik.resetForm({
        values: {
          email: client.email,
          firstName: client.firstName,
          id: client.id,
          isArchived: client.isArchived,
          isDeleted: client.isDeleted,
          lastName: client.lastName,
          propertyIds: [...client.propertyIds],
          serviceProviderId: client.serviceProviderId,
          updatedAt: client.updatedAt,
        },
      });
    }
  }, [client, formik.dirty]);

  const archiveClient = async () => {
    setArchiveLoading(true);
    try {
      const archivedClient = await appsyncOperations.archiveClient({
        id,
        serviceProviderId: client.serviceProviderId,
      });
      dispatch(setClient({ client: archivedClient }));
      enqueueSnackbar("Archive successful!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        typeof error === "string" ? error : "An unexpected error occurred.",
        {
          variant: "error",
        }
      );
    }
    setArchiveLoading(false);
  };
  const restoreClient = async () => {
    setRestoreLoading(true);
    try {
      const restoredClient = await appsyncOperations.restoreClient({
        id,
        serviceProviderId: client.serviceProviderId,
      });
      dispatch(setClient({ client: restoredClient }));
      enqueueSnackbar("Restore successful!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        typeof error === "string" ? error : "An unexpected error occurred.",
        {
          variant: "error",
        }
      );
    }
    setRestoreLoading(false);
  };
  const deleteClient = async () => {
    setDeleteLoading(true);
    try {
      const deletedClient = await appsyncOperations.deleteClient({
        id,
        serviceProviderId: client.serviceProviderId,
      });
      dispatch(setClient({ client: deletedClient }));
      enqueueSnackbar("Delete successful!", { variant: "success" });
      onClose();
    } catch (error) {
      enqueueSnackbar(
        typeof error === "string" ? error : "An unexpected error occurred.",
        {
          variant: "error",
        }
      );
    }
    setDeleteLoading(false);
  };
  const closeAlert = () =>
    setAlertDialog((prevState) => ({ ...prevState, open: false }));
  const attemptDeleteClient = () => {
    if (!(!client.isArchived || !isAdmin || mutationPending || formik.dirty)) {
      setAlertDialog({
        open: true,
        content: "Are you sure you want to delete this client?",
        onYes: () => {
          closeAlert();
          deleteClient();
        },
        onNo: closeAlert,
        onClose: closeAlert,
      });
    }
  };
  const handleContentChange = (content) => {
    setSelectedContent(content);
  };

  const isAdmin = isUserRole({ admin: true });
  const mutationPending =
    archiveLoading || deleteLoading || restoreLoading || formik.isSubmitting;
  const classes = useStyles();

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <DialogTitle>Client Details</DialogTitle>
        <DialogContent classes={{ root: classes.dialogContent }}>
          <div className={classes.contentContainer}>
            <div className={classes.drawer}>
              <List>
                <ConditionalTooltip
                  title={constants.helpText.adminOnlyAction}
                  visible={!isAdmin && !mutationPending}
                >
                  <span>
                    <Button
                      onClick={() => setChangePasswordVisible(true)}
                      classes={{ label: classes.buttonLabel }}
                      color="primary"
                      disabled={!isAdmin || mutationPending || formik.dirty}
                      fullWidth
                      startIcon={<EmailIcon />}
                    >
                      Set Password
                    </Button>
                  </span>
                </ConditionalTooltip>
                <ConditionalTooltip
                  title={constants.helpText.adminOnlyAction}
                  visible={!isAdmin && !mutationPending}
                >
                  {!client.isArchived ? (
                    <span>
                      <Button
                        onClick={archiveClient}
                        classes={{ label: classes.buttonLabel }}
                        color="primary"
                        disabled={!isAdmin || mutationPending || formik.dirty}
                        fullWidth
                        startIcon={
                          archiveLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ArchiveIcon />
                          )
                        }
                      >
                        Archive
                      </Button>
                    </span>
                  ) : (
                    <span>
                      <Button
                        onClick={restoreClient}
                        classes={{ label: classes.buttonLabel }}
                        color="primary"
                        disabled={!isAdmin || mutationPending || formik.dirty}
                        fullWidth
                        startIcon={
                          restoreLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <PersonAddIcon />
                          )
                        }
                      >
                        Restore
                      </Button>
                    </span>
                  )}
                </ConditionalTooltip>
                <ConditionalTooltip
                  title={
                    !client.isArchived
                      ? "Only available if the client has been archived."
                      : constants.helpText.adminOnlyAction
                  }
                  visible={!client.isArchived || !isAdmin}
                >
                  <span>
                    <Button
                      onClick={attemptDeleteClient}
                      classes={{ label: classes.buttonLabel }}
                      color="primary"
                      disabled={
                        !client.isArchived ||
                        !isAdmin ||
                        mutationPending ||
                        formik.dirty
                      }
                      fullWidth
                      startIcon={
                        deleteLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DeleteIcon />
                        )
                      }
                    >
                      Delete
                    </Button>
                  </span>
                </ConditionalTooltip>
              </List>
              <Divider />
              <List>
                <ListSubheader
                  classes={{ gutters: classes.listItemGutters }}
                  disableSticky
                >
                  Information
                </ListSubheader>
                <ListItem
                  classes={{ gutters: classes.listItemGutters }}
                  onClick={() => handleContentChange(content.GENERAL)}
                  button
                  dense
                  disabled={formik.dirty || formik.isSubmitting}
                  selected={selectedContent === content.GENERAL}
                >
                  <ListItemText>General</ListItemText>
                </ListItem>
              </List>
            </div>
            <div className={classes.content}>
              {selectedContent === content.GENERAL && (
                <General
                  formik={formik}
                  admin={isAdmin}
                  disabled={formik.isSubmitting}
                />
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          {!formik.dirty ? (
            <Button onClick={onClose} color="primary">
              Close
            </Button>
          ) : (
            <>
              <LoadingButton
                color="primary"
                onClick={formik.handleSubmit}
                isLoading={formik.isSubmitting}
              >
                Save
              </LoadingButton>
              <Button onClick={() => formik.resetForm()} color="primary">
                Cancel
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <ChangeClientPasswordDialog
        isOpen={changePasswordVisible}
        onClose={() => setChangePasswordVisible(false)}
        clientId={id}
        serviceProviderId={client.serviceProviderId}
      />
      <AlertDialog
        content={alertDialog.content}
        isOpen={alertDialog.open}
        onYes={() => alertDialog.onYes(client)}
        onNo={alertDialog.onNo}
        onClose={alertDialog.onClose}
      />
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: 0,
  },
  contentContainer: {
    display: "flex",
  },
  content: {
    flex: 1,
    paddingBottom: 8,
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 8,
  },
  drawer: {
    width: 240,
    flexShrink: 0,
  },
  bannerTextButtonSeparator: {
    width: 16,
  },
  buttonLabel: {
    justifyContent: "flex-start",
    paddingLeft: 16,
  },
  responseButtonRoot: {
    color: colors.WHITE,
  },
  responseButtonOutlined: {
    border: `1px solid ${colors.WHITE}`,
  },
  listItemGutters: {
    paddingLeft: 24,
    paddingRight: 24,
  },
  bannerContainer: {
    marginBottom: 24,
  },
  bannerContent: {
    height: 72,
    display: "flex",
  },
  bannerText: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    marginLeft: 16,
    marginRight: 16,
    marginTop: 16,
  },
}));

const content = {
  GENERAL: "GENERAL",
};

export default ClientDetailsDialog;
